import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, Typography } from "@mui/material";

import DogMealHeader from "../meals/DogMealHeader";
import PackSummaryCard from "../meals/PackSummaryCard";
import MealRecipeGrid from "../meals/MealRecipeGrid";
import { TrustpilotQuote } from "@/components/quiz/shared/QuizSupport";
import { MEALS_REVIEW } from "../shared/productCopy";
import { QUIZ } from "@/theme/quizTokens";
import {
    autoDistribute,
    filterRecipesByAllergies,
    sortRecipesByPriority,
    type LocalRecipe,
} from "../meals/MealUtils";

import { useDog } from "@/state/useDog";
import { useProducts } from "@/state/ProductsContext";
import {
    getPackPricingConfig,
    buildMealAutoConfigSig,
    type PlanDays,
} from "@/helpers/plansToPacks";
import { GTM } from "@/services/gtm";

type Dog = {
    id: string;
    form: any;
};

type Props = {
    dogs: Dog[];
    activeDogId: string | null;
};

type MealSelectionItem = {
    variantId: string;
    count: number;
};

function DogMealSelection({
  dog,
  allProducts,
}: {
  dog: Dog;
  allProducts: any[];
}) {
  const { form: ctxForm, update } = useDog(dog.id);

  // Prefer context form (canonical source)
  const form = ctxForm || dog.form || {};
  const allergies: string[] = form.allergies ?? [];

  // ✅ Source of truth: the plan is in the saved form
  const selectedPlanDaysRaw = Number(form.selectedPlanDays);
  const planDays: PlanDays = ([7, 14, 28].includes(selectedPlanDaysRaw)
    ? selectedPlanDaysRaw
    : 7) as PlanDays;

  // ✅ Source of truth: meal mode is persisted in form
  const mealMode = (form.mealMode as "FULL" | "MIXED") || "FULL";
  const modeFactor = mealMode === "MIXED" ? 0.6 : 1;

  // ✅ Best source: dailyKey already includes rounding + mealMode factor (if persisted correctly)
  const dailyKeyFromForm = Number(form.dailyKey) || 0;

  // Fallback only if dailyKey is not persisted yet
  const dailyGramsFallback = (Number(form.dailyGrams) || 0) * modeFactor;

  // ✅ Prefer persisted dailyKey; fallback to computed grams
  const gramsForConfig =
    dailyKeyFromForm > 0 ? dailyKeyFromForm : dailyGramsFallback;

  const planConfig = useMemo(() => {
    if (!gramsForConfig)
      return { dailyKey: 0, packSize: 0, totalPacks: 0, totalGrams: 0 };
    return getPackPricingConfig(gramsForConfig, planDays);
  }, [gramsForConfig, planDays]);

  const recommendedPacks = Number(planConfig.totalPacks) || 0;

  // For filtering variants, prefer table packSize
  const resolvedPackSize: number | undefined = planConfig.packSize
    ? Number(planConfig.packSize)
    : form.selectedPackSize
      ? Number(form.selectedPackSize)
      : undefined;

  // Build recipes for this dog, filtered by pack size from plan tables
  const dogRecipes: LocalRecipe[] = useMemo(() => {
    if (!allProducts.length) return [];

    return allProducts
      .flatMap((p: any): LocalRecipe[] => {
        const variants = p.variants ?? [];

        return variants
          .filter((v: any) => {
            const gramsFromTitle = parseInt(v.title, 10);
            const grams =
              v.grams && Number(v.grams) > 0
                ? Number(v.grams)
                : Number.isFinite(gramsFromTitle)
                  ? gramsFromTitle
                  : 0;

            if (!resolvedPackSize) return grams > 0;
            return grams === resolvedPackSize;
          })
          .map((variant: any) => {
            const gramsFromTitle = parseInt(variant.title, 10);
            const grams =
              variant.grams && Number(variant.grams) > 0
                ? Number(variant.grams)
                : Number.isFinite(gramsFromTitle)
                  ? gramsFromTitle
                  : 0;

            return {
              id: String(p.id),
              title: p.title,
              handle: p.handle,
              image: Array.isArray(p.image) ? p.image[0] : p.image,
              variantId: String(variant.id),
              grams,
              price: Number(variant.price),
              variant,
            };
          });
      })
      .filter((r) => !!r && r.grams > 0);
  }, [allProducts, resolvedPackSize]);

  const filteredRecipes = useMemo(
    () => filterRecipesByAllergies(dogRecipes, allergies),
    [dogRecipes, allergies]
  );

  const sortedRecipes = useMemo(
    () => sortRecipesByPriority(filteredRecipes),
    [filteredRecipes]
  );

  // -----------------------------
  // Selection state (per dog)
  // -----------------------------
  const [selection, setSelection] = useState<Record<string, number>>({});

  // Track user edits so we don't keep overriding them
  const userTouchedRef = useRef(false);

  const canComputeSig =
    Number(planConfig.dailyKey) > 0 &&
    Number(planConfig.packSize) > 0 &&
    Number(planConfig.totalPacks) > 0;

  // ✅ Signature for "what plan config am I on?"
  const autoSig = useMemo(() => {
    if (!canComputeSig) return "";

    return buildMealAutoConfigSig({
      planDays,
      dailyKey: Number(planConfig.dailyKey) || 0,
      packSize: Number(planConfig.packSize) || 0,
      totalPacks: Number(planConfig.totalPacks) || 0,
    });
  }, [
    canComputeSig,
    planDays,
    planConfig.dailyKey,
    planConfig.packSize,
    planConfig.totalPacks,
  ]);

  // ✅ Effect A (sync plan-derived fields) — DO NOT set mealAutoConfigSig here
  useEffect(() => {
    const nextPackSize =
      Number(planConfig.packSize) || Number(form.selectedPackSize) || 0;

    const needPackSize =
      nextPackSize > 0 && Number(form.selectedPackSize) !== nextPackSize;

    const needRecommended =
      Number(form.mealRecommendedPacks || 0) !== Number(recommendedPacks || 0);

    if (!needPackSize && !needRecommended) return;

    update({
      ...(needPackSize ? { selectedPackSize: nextPackSize } : null),
      ...(needRecommended ? { mealRecommendedPacks: recommendedPacks } : null),
    });
  }, [
    planConfig.packSize,
    recommendedPacks,
    form.selectedPackSize,
    form.mealRecommendedPacks,
    update,
  ]);

  const didInitRef = useRef(false);

  useEffect(() => {
    if (didInitRef.current) return;

    if (!autoSig) return; // ⬅️ don't init until sig is known
    if (!sortedRecipes.length) return;
    if (!recommendedPacks || recommendedPacks <= 0) return;

    const recipeIds = new Set(sortedRecipes.map((r) => String(r.variantId)));

    const existing = Array.isArray((form as any).mealSelection)
      ? ((form as any).mealSelection as MealSelectionItem[])
      : [];

    const filteredExisting = existing.filter(
      (x) =>
        x?.variantId &&
        Number(x.count) > 0 &&
        recipeIds.has(String(x.variantId))
    );

    const hasFilteredExisting = filteredExisting.length > 0;

    const lastSig = String(form.mealAutoConfigSig || "");
    const matches = lastSig === autoSig;

    // ✅ Case 1: config matches -> load saved selection
    if (matches && hasFilteredExisting) {
      const map: Record<string, number> = {};
      filteredExisting.forEach((item) => {
        map[String(item.variantId)] = Number(item.count);
      });

      // Rebuild to the plan total when saved selections were dropped (e.g. a
      // recipe was filtered out after an allergy change): redistribute the
      // freed pots across the remaining safe recipes, priority-first.
      let usedLocal = Object.values(map).reduce((s, v) => s + (v || 0), 0);
      const shortfall = recommendedPacks - usedLocal;
      if (shortfall > 0 && sortedRecipes.length) {
        for (let i = 0; i < shortfall; i++) {
          const r = sortedRecipes[i % sortedRecipes.length];
          const id = String(r.variantId);
          map[id] = (map[id] || 0) + 1;
        }
        usedLocal = recommendedPacks;
      }

      setSelection(map);

      // ✅ persist the (possibly rebuilt) selection so stale entries for
      // filtered-out recipes never survive into checkout
      const arr: MealSelectionItem[] = Object.entries(map)
        .filter(([, count]) => count > 0)
        .map(([variantId, count]) => ({ variantId, count }));

      update({
        mealSelection: arr,
        mealSelectedPacks: usedLocal,
        mealRecommendedPacks: recommendedPacks,
      });

      didInitRef.current = true;
      return;
    }

    // ✅ Case 2: config mismatch OR nothing saved -> reseed now
    userTouchedRef.current = false;

    const base = autoDistribute(sortedRecipes, recommendedPacks);
    setSelection(base);

    const arr: MealSelectionItem[] = Object.entries(base)
      .filter(([, count]) => count > 0)
      .map(([variantId, count]) => ({ variantId, count }));

    const usedLocal = Object.values(base).reduce((sum, v) => sum + (v || 0), 0);

    update({
      mealSelection: arr,
      mealSelectedPacks: usedLocal,
      mealRecommendedPacks: recommendedPacks,
      mealAutoConfigSig: autoSig, // ✅ only set sig when selection matches it
    });

    didInitRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    autoSig,
    sortedRecipes,
    recommendedPacks,
    form.mealSelection,
    form.mealAutoConfigSig,
    update,
  ]);

  // ✅ Only reset/init when sig is valid; avoids clearing during transient empty sig
  useEffect(() => {
    if (!autoSig) return;
    didInitRef.current = false;
    setSelection({});
  }, [autoSig]);

  const used = useMemo(
    () => Object.values(selection).reduce((sum, v) => sum + (v || 0), 0),
    [selection]
  );

  const resetSelection = () => {
    if (!sortedRecipes.length || !recommendedPacks || recommendedPacks <= 0) return;

    userTouchedRef.current = false;

    const base = autoDistribute(sortedRecipes, recommendedPacks);
    setSelection(base);

    const arr: MealSelectionItem[] = Object.entries(base)
      .filter(([, count]) => count > 0)
      .map(([variantId, count]) => ({ variantId, count }));

    const usedLocal = Object.values(base).reduce((sum, v) => sum + (v || 0), 0);

    update({
      mealSelection: arr,
      mealSelectedPacks: usedLocal,
      mealRecommendedPacks: recommendedPacks,
      // ✅ make selection + sig consistent immediately
      ...(autoSig ? { mealAutoConfigSig: autoSig } : null),
    });
  };

  // ✅ Guard: never commit if we don't have a valid recommended count yet
  const commitSelection = (next: Record<string, number>) => {
    if (!recommendedPacks || recommendedPacks <= 0) return;

    const arr: MealSelectionItem[] = Object.entries(next)
      .filter(([, c]) => c > 0)
      .map(([variantId, count]) => ({ variantId, count }));

    const usedLocal = Object.values(next).reduce((s, v) => s + (v || 0), 0);

    update({
      mealSelection: arr,
      mealSelectedPacks: usedLocal,
      mealRecommendedPacks: recommendedPacks,
      ...(autoSig ? { mealAutoConfigSig: autoSig } : null),
    });
  };

  const increment = (variantId: string) => {
    userTouchedRef.current = true;
    setSelection((prev) => {
      const next = { ...prev, [variantId]: (prev[variantId] ?? 0) + 1 };
      commitSelection(next);

      // Track meal selection
      const recipe = sortedRecipes.find(r => r.variantId === variantId);
      if (recipe) {
        const totalSelected = Object.values(next).reduce((sum, v) => sum + (v || 0), 0);
        const wasComplete = used >= recommendedPacks;
        const isNowComplete = totalSelected >= recommendedPacks;

        GTM.trackMealSelected({
          meal: {
            variantId: recipe.variantId,
            recipeName: recipe.title,
            quantity: next[variantId],
            price: recipe.price,
          },
          dogId: dog.id,
          dogName: form.name,
          totalSelected,
          recommended: recommendedPacks,
        });

        // Track when selection becomes complete
        if (!wasComplete && isNowComplete) {
          const meals = sortedRecipes
            .filter(r => next[r.variantId] > 0)
            .map(r => ({
              variantId: r.variantId,
              recipeName: r.title,
              quantity: next[r.variantId],
              price: r.price,
            }));

          GTM.trackMealSelectionCompleted({
            dogId: dog.id,
            dogName: form.name,
            meals,
            totalPacks: totalSelected,
            recommendedPacks,
          });
        }
      }

      return next;
    });
  };

  const decrement = (variantId: string) => {
    userTouchedRef.current = true;
    setSelection((prev) => {
      const current = prev[variantId] ?? 0;
      if (current <= 0) return prev;

      const next = { ...prev, [variantId]: current - 1 };
      if (next[variantId] === 0) {
        delete next[variantId];
      }
      commitSelection(next);

      // Track meal deselection
      const recipe = sortedRecipes.find(r => r.variantId === variantId);
      if (recipe) {
        const totalSelected = Object.values(next).reduce((sum, v) => sum + (v || 0), 0);

        GTM.trackMealSelected({
          meal: {
            variantId: recipe.variantId,
            recipeName: recipe.title,
            quantity: next[variantId] || 0,
            price: recipe.price,
          },
          dogId: dog.id,
          dogName: form.name,
          totalSelected,
          recommended: recommendedPacks,
        });
      }

      return next;
    });
  };

  return (
    <Box sx={{ mb: { xs: 6, md: 10 } }}>
      {/* Header: dog name + reset */}
      <DogMealHeader dogName={form.name || "Your Dog"} onReset={resetSelection} />

      {/* Kuillo sub-head (Figma: "Pick 12 meals") */}
      {recommendedPacks > 0 && (
        <Typography
          sx={{
            fontFamily: QUIZ.font.label,
            fontSize: { xs: 21, md: 24 },
            color: QUIZ.color.ink,
            textAlign: "center",
            mb: { xs: "18px", md: "22px" },
          }}
        >
          Pick {recommendedPacks} meals
        </Typography>
      )}

      {/* Pack Summary */}
      <PackSummaryCard
        used={used}
        recommended={recommendedPacks}
        dogName={form.name}
      />

      {/* Grid of recipes */}
      <MealRecipeGrid
        recipes={sortedRecipes}
        selection={selection}
        onIncrement={increment}
        onDecrement={decrement}
      />
    </Box>
  );
}

export default function ResultsMealSelection({ dogs }: Props) {
  const { mealProducts } = useProducts();

  // Issue #50: "Edit [Dog]'s meals" on the review step drops a one-shot flag
  // and jumps here — scroll that dog's section into view. Deliberately NOT
  // keyed off activeDogId: it is always backfilled, so that would also scroll
  // past the step header on every normal Plan → Meals advance.
  useEffect(() => {
    let target: string | null = null;
    try {
      target = sessionStorage.getItem("paws_scroll_to_dog");
      if (target) sessionStorage.removeItem("paws_scroll_to_dog");
    } catch { }
    if (!target || dogs.length < 2) return;
    const el = document.getElementById(`meal-dog-${target}`);
    if (el) el.scrollIntoView({ block: "start" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!mealProducts.length) {
    return (
      <Box sx={{ textAlign: "center", py: 6, px: 2 }}>
        <Typography sx={{ fontSize: "1.1rem", fontWeight: 600, color: "#4A3428", mb: 1 }}>
          No meals available
        </Typography>
        <Typography sx={{ fontSize: "0.9rem", color: "#666" }}>
          Please refresh the page or contact support if this persists.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ px: { xs: 2, md: 4 } }}>
      {/* Trustpilot quote card (Figma 14) */}
      <Box
        sx={{
          bgcolor: "#e9dfd7",
          borderRadius: "16px",
          px: { xs: "20px", md: "32px" },
          py: { xs: "22px", md: "28px" },
          maxWidth: 640,
          mx: "auto",
          mb: { xs: "34px", md: "44px" },
          display: "flex",
          justifyContent: "center",
        }}
      >
        <TrustpilotQuote {...MEALS_REVIEW} />
      </Box>

      {/* Headings */}
      <Box sx={{ textAlign: "center", mb: { xs: "26px", md: "36px" } }}>
        <Typography
          component="h1"
          sx={{
            fontFamily: QUIZ.font.display,
            fontWeight: 900,
            textTransform: "uppercase",
            color: QUIZ.color.ink,
            fontSize: { xs: "34px", md: "41px" },
            lineHeight: 1.1,
          }}
        >
          Pick your meals
        </Typography>
        <Typography
          sx={{
            fontFamily: QUIZ.font.body,
            fontSize: { xs: 16, md: 17 },
            color: QUIZ.color.ink,
            opacity: 0.9,
            mt: "8px",
          }}
        >
          Choose their favourite.
        </Typography>
      </Box>

      {dogs.map((dog) => (
        <Box key={dog.id} id={`meal-dog-${dog.id}`} sx={{ scrollMarginTop: "110px" }}>
          <DogMealSelection dog={dog} allProducts={mealProducts} />
        </Box>
      ))}
    </Box>
  );
}